<template>
  <div v-if="currentTutorial" class="tutorial-page">
    <Loader v-if="isLoading" visible/>

    <Notification
        v-if="notificationMessage"
        :message="notificationMessage"
        :type="notificationType"
        :duration="3000"
        @closed="notificationMessage = null"
    />

    <FullWidthPhotoHeader
        :tutorial="currentTutorial"
        :active-editor="activeEditor"
        :description-editor="descriptionEditor"
        :description="currentTutorial.description"
        :updatePublished="updatePublished"
        @photo-upload="handlePhotoUpload"
        @update:title="handleTitleUpdate"
        @update:active-editor="toggleEditor"
    />
    <EditorCloseButton v-if="activeEditor" :onClose="() => toggleEditor(null)" />

    <TextToolbar :editor="textEditor"
                 :editMode="editText"
                 :copyTextColor="copyTextColor"
                 :fullWidthHeaderColor="fullWidthHeaderColor"
                 :textColor="textColor"
                 folder-name="tutorials/texts"
                 @update:copyTextColor="color => copyTextColor = color"
                 @update:textColor="color => textColor = color"
                 @update:fullWidthHeaderColor="color => fullWidthHeaderColor = color"
    />

    <SimpleToolbar :editor="descriptionEditor"
                 :editMode="editDescription"
    />

    <div class="tutorial-content">
      <div class="content-container">
        <!-- Левая колонка (реклама) -->
        <div class="side-column hide-on-mobile">
          <div class="ad-placeholder">
            <span>Здесь могла бы быть ваша реклама</span>
          </div>
        </div>

        <!-- Центральная колонка (основной контент) -->
        <div class="main-content">
          <div class="text-editor-container">
            <button
              v-if="isModerator && activeEditor !== 'text'"
              class="edit-button cosmic-button"
              @click="toggleEditor('text')"
            >
              ✏️
            </button>

            <editor-content
                v-if="activeEditor === 'text'"
                :editor="textEditor"
                class="editor content-editor"
            />

            <div
                v-else
                class="content-text"
                ref="contentContainer"
                :key="currentTutorial.text"
                v-html="editorContent"
            />
          </div>
        </div>

        <!-- Правая колонка (реклама) -->
        <div class="side-column hide-on-mobile">
          <div class="ad-placeholder">
            <span>Здесь могла бы быть ваша реклама</span>
          </div>
        </div>
      </div>

      <!-- Кнопки действий -->
      <div class="actions-container" v-if="activeEditor && isContentChanged">
        <UpdateButton
            :active-editor="activeEditor"
            :is-content-changed="isContentChanged"
            @update-tutorial="updateTutorial"
            @close-editor="toggleEditor(null)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {Editor, EditorContent} from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import CodeBlock from "@tiptap/extension-code-block";
import Color from "@tiptap/extension-color";
import TutorialDataService from "../../../services/TutorialDataService.js";
import Prism from 'prismjs';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-java';
import 'prismjs/themes/prism-tomorrow.css';

import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import ContentPageTitle from "../editor/ContentPageTitle.vue";
import ContentPageInfo from "../editor/ContentPageInfo.vue";
import TextToolbar from "../editor/toolbar/TextToolbar.vue";
import ContentPageField from "../editor/ContentPageTextField.vue";
import FullWidthPhotoHeader from "../editor/addons/FullWidthPhotoHeader.vue";
import {Node} from "@tiptap/core";
import Image from "@tiptap/extension-image";
import UpdateButton from "../editor/buttons/UpdateButton.vue";
import Loader from "../Loader.vue";
import EditorCloseButton from "../editor/buttons/EditorCloseButton.vue";
import SimpleToolbar from "../editor/toolbar/SimpleToolbar.vue";
import Notification from "../Notification.vue";
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import {mapGetters} from "vuex";
import { s3Service } from "../../../services/s3Service.js";
import { notificationMixin } from "../../../mixins/notificationMixin";

const CustomCodeBlock = CodeBlock.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: 'copy-class', // Добавляем класс по умолчанию
        renderHTML: (attributes) => ({
          class: `${attributes.class || ''} copy-class`.trim(),
        }),
      },
    };
  },
});

const FullWidthHeader = Node.create({
  name: "fullWidthHeader",
  group: "block",
  content: "inline*",
  defining: true,
  parseHTML() {
    return [{tag: "div[data-type=full-width-header]"}];
  },
  renderHTML({HTMLAttributes}) {
    return ["div", {...HTMLAttributes, "data-type": "full-width-header"}, 0];
  },
  addAttributes() {
    return {
      backgroundColor: {
        default: "#e0e0e5",
        parseHTML: (element) => element.style.backgroundColor || "#e0e0e5",
        renderHTML: (attributes) => ({
          style: `background-color: ${attributes.backgroundColor}`
        })
      },
      textAlign: {
        default: "#e0e0e5",
        parseHTML: (element) => element.style.textAlign || "left",
        renderHTML: (attributes) => ({
          style: `text-align: ${attributes.textAlign}`
        })
      }
    };
  },
});

const CopyText = Node.create({
  name: "copyText",
  group: "block",
  content: "inline*", // Позволяет содержать span
  defining: true,
  parseHTML() {
    return [
      {
        tag: 'div.copy-text[data-type=copy-text]'
      },
    ];
  },
  renderHTML({ HTMLAttributes, children }) {
    return [
      "div",
      { ...HTMLAttributes, "data-type": "copy-text", class: "copy-text" },
      ["span", { class: "copy-text" }, 0], // Добавляем класс к span
    ];
  },
  // renderHTML({HTMLAttributes}) {
  //   return ["div", {...HTMLAttributes, "data-type": "copy-text", "class": "copy-text"}, 0];
  // },

  // addAttributes() {
  //   return {
  //     backgroundColor: {
  //       default: "#e0e0e5",
  //       parseHTML: (element) => element.style.backgroundColor || "#e0e0e5",
  //       renderHTML: (attributes) => ({
  //         style: `background-color: ${attributes.backgroundColor}`
  //       })
  //     }
  //   };
  // },
});

// const CopyText = Node.create({
//   name: "copyText",
//   group: "block",
//   content: "inline*",
//   defining: true,
//   parseHTML() {
//     return [
//       {
//         tag: 'p.copy-text',
//         getAttrs: (element) => ({
//           backgroundColor: element.style.backgroundColor || "#e0e0e5",
//         }),
//       },
//     ];
//   },
//   renderHTML({HTMLAttributes}) {
//     return ["p", {...HTMLAttributes, "class": "copy-text"}, 0];
//   },
//   addAttributes() {
//     return {
//       backgroundColor: {
//         default: "#e0e0e5",
//         parseHTML: (element) => element.style.backgroundColor || "#e0e0e5",
//         renderHTML: (attributes) => ({
//           style: `background-color: ${attributes.backgroundColor}`
//         })
//       }
//     };
//   },
// });

const EditableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        renderHTML: (attributes) => ({
          src: attributes.src,
        }),
        parseHTML: (element) => element.getAttribute("src"),
      },
    };
  },
});


export default {
  name: "Tutorial",
  components: {
    Notification,
    SimpleToolbar,
    EditorCloseButton,
    Loader,
    UpdateButton,
    FullWidthPhotoHeader, ContentPageField, TextToolbar, ContentPageInfo, ContentPageTitle, EditorContent},
  data() {
    return {
      currentTutorial: null,
      isLoading: false,
      message: "",
      notificationMessage: null,
      notificationType: "info",
      activeEditor: null,
      editText: false,
      editDescription: false,
      descriptionEditor: null,

      textEditor: new Editor({
        extensions: [
          StarterKit,
          TextStyle,
          Color,
          TextAlign.configure({
            types: ['heading', 'paragraph'],
          }),
          Image.configure({
            HTMLAttributes: {
              class: 'editor-image',
            },
            addAttributes() {
              return {
                ...this.parent?.(),
                src: {
                  default: null,
                  parseHTML: element => element.getAttribute('src'),
                  renderHTML: attributes => {
                    if (attributes.src && attributes.src.includes('yadi.sk')) {
                      return null;
                    }
                    return {
                      src: attributes.src,
                    };
                  },
                },
              };
            },
            uploadImage: async (file) => {
              try {
                const url = await s3Service.uploadEditorImage(file);
                return url;
              } catch (error) {
                console.error('Error uploading image:', error);
                throw error;
              }
            }
          }),
          EditableImage.configure({
            inline: true,
            editable: this.editText
          }),
          CustomCodeBlock.configure({language: "java"}),
          CustomCodeBlock.configure({language: "bash"}),
          CopyText,
          FullWidthHeader,
          Underline,
          Link.configure({
            openOnClick: true,
            autolink: true,
          }),
        ],
        onUpdate: ({editor}) => {
          this.currentTutorial.text = editor.getHTML();
        },
      }),
      fullWidthHeaderColor: "#e0e0e5",
      textColor: "#000000",
    };
  },
  methods: {
    addLinkClickHandler() {
      const container = this.$refs.contentContainer;

      // const container = this.$el.querySelector('.content-text');


      if (container) {
        container.addEventListener('click', (event) => {
          const target = event.target;

          // Проверяем, является ли элемент ссылкой
          if (target.tagName === 'A' && target.href) {
            event.preventDefault(); // Останавливаем стандартное действие
            const url = target.getAttribute('href');

            // Запрашиваем подтверждение
            if (window.confirm('Вы уверены, что хотите перейти по внешней ссылке?')) {
              window.open(url, '_blank'); // Открываем ссылку в новой вкладке
            }
          }
        });
      }
    },
    handleCopyTextClick(event) {
      const target = event.target;
      if (target.classList.contains('copy-text')) {
        const text = target.innerText;
        navigator.clipboard.writeText(text)
            .then(() => this.showNotification('Текст скопирован!', 'success'))
            .catch(err => console.error('Ошибка копирования:', err));
      }

      if (target.tagName === 'a') {
        event.preventDefault(); // Останавливаем переход по ссылке
        // const url = target.getAttribute('href');
        //
        // if (url && window.confirm('Вы уверены, что хотите перейти по внешней ссылке?')) {
        //   window.open(url, '_blank'); // Открываем ссылку в новой вкладке
        // }
      }
    },
//     insertCopyText() {
//       this.textEditor.chain().focus().insertContent({
//         type: "copyText",
//         attrs: { text: "Текст для копирования" },
//       }).run();
//     },
//      copyToClipboard(element) {
//   const text = element.innerText;
//   navigator.clipboard.writeText(text)
//       .then(() => alert('Текст скопирован!'))
//       .catch(err => console.error('Ошибка копирования:', err));
// },
    showNotification(message, type = "info") {
      this.notificationMessage = message;
      this.notificationType = type;
    },
    toggleEditor(editorType) {
      // Если закрываем редактор и нет изменений, сбрасываем к оригинальному состоянию
      if (this.activeEditor && !editorType && !this.isContentChanged) {
        this.currentTutorial.title = this.originalTitle;
        this.currentTutorial.description = this.originalDescription;
        this.currentTutorial.text = this.originalText;
        this.textEditor.commands.setContent(this.originalText);
      }
      this.activeEditor = this.activeEditor === editorType ? null : editorType;
    },
    handleKeyDown(event) {
      if (event.key === "Escape" && this.activeEditor) {
        this.toggleEditor(null);
      }
    },
    async getTutorial(id) {
      if (!id) return;

      try {
        this.isLoading = true;
        const response = await TutorialDataService.get(id);
        this.currentTutorial = response.data;

        // Обрабатываем фото в шапке
        if (this.currentTutorial.photo) {
          try {
            this.currentTutorial.photo = await s3Service.getDownloadLink(this.currentTutorial.photo);
          } catch (photoError) {
            console.error("Error loading photo:", photoError);
            this.showNotification("Failed to load photo", "error");
          }
        }

        // Обрабатываем фото в тексте
        if (this.currentTutorial.text) {
          try {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = this.currentTutorial.text;
            const images = tempDiv.getElementsByTagName('img');

            console.log('Loading images into cache...');

            for (let img of images) {
                const src = img.getAttribute('src');
                console.log('Processing src for cache:', src);

                if (src && s3Service.isS3Link(src)) {
                    try {
                        const downloadUrl = await s3Service.getDownloadLink(src);
                        console.log('Cached download URL:', downloadUrl);
                    } catch (imgError) {
                        console.error("Error loading image from text:", imgError);
                    }
                }
            }

            // Обновляем текст с обработанными ссылками
            this.currentTutorial.text = tempDiv.innerHTML;
          } catch (textError) {
            console.error("Error processing text images:", textError);
            this.showNotification("Failed to load some images in text", "error");
          }
        }

        this.textEditor.commands.setContent(this.currentTutorial.text || "");
        this.initEditors();
        this.originalTitle = response.data.title;
        this.originalDescription = response.data.description;
        this.originalText = response.data.text;
      } catch (e) {
        console.error(e);
        this.showNotification("Failed to load tutorial", "error");
      } finally {
        this.isLoading = false;
      }
    },
    async updateTutorial() {
      // Если есть ссылки в формате [Image: url], преобразуем их обратно в img теги
      if (this.currentTutorial.text) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = this.currentTutorial.text;

          const links = tempDiv.getElementsByTagName('a');
          Array.from(links).forEach(link => {
              const text = link.textContent;
              if (text.startsWith('[Image: ') && text.endsWith(']')) {
                  const src = text.slice(8, -1); // Убираем '[Image: ' и ']'
                  const img = document.createElement('img');
                  img.src = src;
                  link.parentNode.replaceChild(img, link);
              }
          });

          this.currentTutorial.text = tempDiv.innerHTML;
      }

      this.$emit('set-loading', true);
      try {
          await TutorialDataService.update(this.currentTutorial.id, this.currentTutorial);
          this.editDescription = false;
          this.showNotification("The tutorial was updated successfully!", "success");
          await this.getTutorial(this.currentTutorial.id);
      } catch (e) {
          console.log(e);
          this.showNotification("Failed to update the tutorial.", "error");
      } finally {
          this.$emit('set-loading', false);
      }
    },
    async handlePhotoUpload(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
          const publicUrl = await s3Service.uploadFile(
              file,
              'tutorials'
          );

          const updatedTutorial = {
              ...this.currentTutorial,
              photo: publicUrl
          };

          const response = await TutorialDataService.update(
              this.currentTutorial.id,
              updatedTutorial
          );

          if (response.status === 200) {
              this.showNotification("Photo uploaded successfully!", "success");
              await this.getTutorial(this.currentTutorial.id);
          }
      } catch (error) {
          console.error("Error uploading photo:", error);
          this.showNotification(error.message || "Failed to upload photo", "error");
      }
    },
    handleTitleUpdate(newTitle) {
      this.currentTutorial.title = newTitle;
      this.updateTitle(newTitle);
      this.toggleEditor(null);

    },
    updateTitle(newTitle) {
      const id = this.$route.params.id;
      const data = {
        ...this.currentTutorial,
        title: newTitle
      };

      TutorialDataService.update(id, data)
          .then(() => {
            this.showNotification("Title updated successfully!", "success");

          })
          .catch((e) => {
            console.log(e);
          });
    },
    updatePublished(status) {
      const data = {
        ...this.currentTutorial,
        published: status
      };
      TutorialDataService.update(this.currentTutorial.id, data)
          .then(() => {
            this.currentTutorial.published = status;
            this.showNotification("Status updated successfully!", "success");
          })
          .catch((e) => {
            console.log(e);
          });
    },

    initEditors() {
      this.descriptionEditor = new Editor({
        extensions: [StarterKit, Color, Underline],
        content: this.currentTutorial.description || "",
        onUpdate: ({editor}) => {
          this.currentTutorial.description = editor.getHTML();
        },
        editable: this.editDescription,
      });
    },
    highlightCode() {
      this.$nextTick(() => {
        if (this.$el) {  // Добавляем проверку
            const codeBlocks = this.$el.querySelectorAll('pre code');
            codeBlocks.forEach((block) => {
                Prism.highlightElement(block);
            });
        }
      });
    },


    // handleContainerClick(event) {
    //   const target = event.target;
    //   if (target.classList.contains('copy-text')) {
    //     const text = target.innerText;
    //     navigator.clipboard.writeText(text)
    //         .then(() => this.showNotification('Текст скопирован!', 'success'))
    //         .catch(err => console.error('Ошибка копирования:', err));
    //   }
    // }
    handleContainerClick(event) {
      const target = event.target;
      if (target.classList.contains('copy-text')) {
        const text = target.innerText;
        if (navigator.clipboard) {
          navigator.clipboard.writeText(text)
              .then(() => this.showNotification('Текст скопирован!', 'success'))
              .catch(err => console.error('Ошибка копирования:', err));
        } else {
          console.error('Clipboard API недоступен');
        }
      }
    },
    async processImages() {
        if (!this.currentTutorial?.text) return;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.currentTutorial.text;
        const images = tempDiv.getElementsByTagName('img');

        for (let img of images) {
            const src = img.getAttribute('src');
            if (src && s3Service.isS3Link(src)) {
                try {
                    const downloadUrl = await s3Service.getDownloadLink(src);
                    img.setAttribute('src', downloadUrl);
                } catch (error) {
                    console.error('Error processing image:', error);
                }
            }
        }

        // Обновляем текст с обработанными ссылками
        this.currentTutorial.text = tempDiv.innerHTML;
    }

  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'isModerator', 'hasRole']),

    isContentChanged() {
        if (!this.currentTutorial) return false;

        // Преобразуем null в пустую строку для корректного сравнения
        const currentText = this.currentTutorial.text || '';
        const originalText = this.originalText || '';
        const currentDescription = this.currentTutorial.description || '';
        const originalDescription = this.originalDescription || '';

        return (
            this.currentTutorial.title !== this.originalTitle ||
            currentDescription !== originalDescription ||
            currentText !== originalText
        );
    },

    processedText() {
        return this.currentTutorial?.text || '';
    },

    editorContent() {
        if (!this.currentTutorial?.text) return '';

        // Если режим редактирования, заменяем img теги на ссылки
        if (this.activeEditor === 'text') {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = this.currentTutorial.text;

            const images = tempDiv.getElementsByTagName('img');
            // Преобразуем HTMLCollection в массив, так как при замене элементов коллекция будет динамически меняться
            Array.from(images).forEach(img => {
                const src = img.getAttribute('src');
                const link = document.createElement('a');
                link.href = src;
                link.textContent = `[Image: ${src}]`;
                link.target = '_blank';
                img.parentNode.replaceChild(link, img);
            });

            return tempDiv.innerHTML;
        }

        // В режиме просмотра возвращаем обработанный текст с изображениями
        return this.processedText;
    }

  },
  mounted() {

    this.getTutorial(this.$route.params.id);
    window.addEventListener("scroll", this.handleScroll);
    this.highlightCode();

    // this.addLinkClickHandler();


    this.$nextTick(() => {
      const container = this.$el.querySelector('.content-text');
      if (container) {
        container.addEventListener('click', (event) => {
          const target = event.target;
          if (target.classList.contains('copy-text')) {
            const text = target.innerText;
            navigator.clipboard.writeText(text)
                .then(() => this.showNotification('Текст скопирован!', 'success'))
                .catch(err => console.error('Ошибка копирования:', err));
          }

          if (target.tagName === 'A' && target.href) {
            event.preventDefault(); // Останавливаем стандартное действие
            const url = target.getAttribute('href');

            // Запрашиваем подтверждение
            if (window.confirm('Вы уверены, что хотите перейти по внешней ссылке?')) {
              window.open(url, '_blank'); // Открываем ссылку в новой вкладке
            }
          }
        });
      } else {
        console.error('Элемент .content-text не найден');
      }
    });

    this.highlightCode();

    // const container = this.$el.querySelector('.content-text');
    // if (container) {
    //   container.addEventListener('click', this.handleContainerClick);
    // }
    // document.addEventListener('click', this.handleContainerClick);



    // this.$nextTick(() => {
    //   const container = this.$el.querySelector('.content-text');
    //   if (container && !container.dataset.listenerAttached) {
    //     container.addEventListener('click', this.handleContainerClick);
    //     container.dataset.listenerAttached = true; // Метка для предотвращения дублирования
    //   }
    // });

    if (this.currentTutorial) {
      this.originalTitle = this.currentTutorial.title;
      this.originalDescription = this.currentTutorial.description;
      this.originalText = this.currentTutorial.text;
    }

    window.addEventListener("scroll", this.handleScroll);
    document.addEventListener("keydown", this.handleKeyDown);

    this.processImages();

  },
  updated() {
    this.highlightCode();
  },
  beforeRouteUpdate(to, from, next) {
    this.$nextTick(() => {
      console.log("Updated Base64 image:", this.base64Image2);
      this.highlightCode();
      next();
    });
  },
  beforeUnmount() {
    this.descriptionEditor && this.descriptionEditor.destroy();
    this.textEditor && this.textEditor.destroy();
    window.removeEventListener("scroll", this.handleScroll);
    document.removeEventListener("keydown", this.handleKeyDown);
    s3Service.clearCache(); // Очищаем кэш при размонтировании
  },
  watch: {
    'currentTutorial.text': {
        immediate: true,
        async handler() {
            await this.processImages();
        }
    },
    activeEditor(newEditor) {
      this.editText = newEditor === 'text';
      this.editDescription = newEditor === 'description';
    },
    editDescription(newVal) {
      this.descriptionEditor.setOptions({editable: newVal});
    },
    editText(newVal) {
      this.textEditor.setOptions({editable: newVal});
    },
    // 'currentTutorial.textWithBase64'(newValue) {
    //   this.$nextTick(() => {
    //     const container = this.$el.querySelector('.content-text');
    //     if (container && !container.dataset.listenerAttached) {
    //       container.addEventListener('click', this.handleContainerClick);
    //       container.dataset.listenerAttached = true;
    //     }
    //   });
    // }
  },
};
</script>

<style scoped>
.tutorial-page {
  min-height: 100vh;
}

.tutorial-content {
  margin: 0 0;
}

.content-container {
  display: flex;
  width: 100% !important;
  gap: 10px;
  margin-top: 10px;
}

.side-column {
  flex: 0 0 200px;
}

.main-content {
  flex: 1;
  min-width: 0;

}

.ad-placeholder {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 20px;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--my-light);
  opacity: 0.5;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 80px;
}

.text-editor-container {
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  overflow: hidden;
}

.edit-button {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
  background: var(--my-links);
  color: var(--my-light);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.edit-button:hover {
  background: var(--my-accent);
  transform: translateY(-2px);
}

.content-editor {
  background: var(--my-dark);
  border-radius: 8px;
  padding: 25px;
  min-height: 300px;
}

.content-text {
  padding: 25px;
  color: var(--my-light);
  line-height: 1.6;
  min-height: 600px;
}

/* Стили для кода внутри контента */
:deep(pre) {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 20px;
  margin: 15px 0;
  overflow-x: auto;
}

:deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 15px 0;
}

.actions-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1100;
  display: flex;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 15px 20px;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
}

.actions-container :deep(button) {
  pointer-events: auto;
  width: 100%;
  max-width: 1200px;
}

@media (max-width: 1200px) {
  .side-column {
    flex: 0 0 150px;
  }
}

@media (max-width: 990px) {
  .side-column.hide-on-mobile {
    display: none;
  }

  .main-content {
    flex: 0 0 100%;
  }
}

@media (max-width: 768px) {
  .tutorial-content {
    padding: 0;
  }

  .content-text {
    padding: 20px;
  }

  .edit-button {
    width: 35px;
    height: 35px;
    top: 15px;
    right: 15px;
  }

  .actions-container {
    padding: 10px;
  }
}
</style>
